<template>
  <div class="min-h-screen bg-gray-50">
    <!-- Header -->
    <header class="bg-white border-b border-gray-200 sticky top-0 z-10">
      <div class="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
        <div>
          <h1 class="text-lg font-bold text-gray-900">Meus Pedidos</h1>
          <p v-if="driverName" class="text-sm text-gray-500">{{ driverName }}</p>
        </div>
        <button
          @click="logout"
          class="text-sm text-red-600 hover:text-red-800 font-medium"
        >
          Sair
        </button>
      </div>
    </header>
    
    <!-- Content -->
    <main class="max-w-3xl mx-auto px-4 py-6">
      <!-- Loading -->
      <div v-if="loading" class="text-center py-12 text-gray-500">
        Carregando pedidos...
      </div>
      
      <!-- Empty -->
      <div v-else-if="orders.length === 0" class="text-center py-12">
        <div class="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
          <svg class="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"></path>
          </svg>
        </div>
        <p class="text-gray-500">Nenhum pedido pendente de entrega</p>
      </div>
      
      <!-- Orders List -->
      <div v-else class="space-y-4">
        <div
          v-for="order in orders"
          :key="order.id"
          class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden"
        >
          <div class="p-4">
            <div class="flex justify-between items-start mb-3">
              <div>
                <p class="text-xs text-gray-500 uppercase tracking-wide">Pedido</p>
                <p class="text-lg font-bold text-gray-900">#{{ order.order_number }}</p>
              </div>
              <span
                class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                :class="getStatusClass(order.status)"
              >
                {{ getStatusLabel(order.status) }}
              </span>
            </div>
            
            <div class="space-y-2 mb-4">
              <div class="flex items-start gap-2">
                <svg class="w-4 h-4 text-orange-600 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
                </svg>
                <div>
                  <p class="text-sm font-medium text-gray-900">{{ formatAddress(order) }}</p>
                </div>
              </div>
              
              <div class="flex items-center gap-2">
                <svg class="w-4 h-4 text-gray-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
                </svg>
                <p class="text-sm text-gray-700">{{ order.customer_name || 'N/A' }}</p>
              </div>
              
              <div class="flex items-center gap-2">
                <svg class="w-4 h-4 text-gray-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                <p class="text-sm text-gray-700">{{ formatDateTime(order.created_at) }}</p>
              </div>
            </div>
            
            <button
              @click="deliverOrder(order)"
              :disabled="deliveringId === order.id"
              class="w-full px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 font-medium flex items-center justify-center gap-2"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
              </svg>
              {{ deliveringId === order.id ? 'Registrando...' : 'Registrar Entrega' }}
            </button>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { driverAuthApi } from '@/utils/api'
import { notifyError, notifySuccess } from '@/utils/notifications'
import { confirmDialog } from '@/composables/useConfirm'

interface DriverOrder {
  id: number
  order_number: string
  status: string
  total: number
  customer_name: string
  customer_phone: string
  delivery_address?: string
  delivery_neighborhood?: string
  delivery_city?: string
  delivery_complement?: string
  created_at: string
}

const router = useRouter()
const orders = ref<DriverOrder[]>([])
const loading = ref(true)
const deliveringId = ref<number | null>(null)
const driverName = ref(localStorage.getItem('driver_name') || '')

const checkAuth = () => {
  const token = localStorage.getItem('driver_token')
  if (!token) {
    router.push('/driver/login')
    return false
  }
  return true
}

const loadOrders = async () => {
  if (!checkAuth()) return
  
  loading.value = true
  try {
    const res = await driverAuthApi.getMyOrders()
    orders.value = res.data
  } catch (error: any) {
    if (error.response?.status === 401) {
      logout()
    } else {
      notifyError('Erro ao carregar pedidos')
    }
  } finally {
    loading.value = false
  }
}

const deliverOrder = async (order: DriverOrder) => {
  if (!await confirmDialog({ title: 'Confirmar entrega', message: `Confirmar entrega do pedido #${order.order_number}?`, danger: false, confirmLabel: 'Confirmar entrega' })) return
  
  deliveringId.value = order.id
  try {
    await driverAuthApi.deliverOrder(order.id)
    notifySuccess('Entrega registrada com sucesso!')
    loadOrders()
  } catch (error: any) {
    notifyError(error.response?.data?.detail || 'Erro ao registrar entrega')
  } finally {
    deliveringId.value = null
  }
}

const formatAddress = (order: DriverOrder): string => {
  const parts = [
    order.delivery_address,
    order.delivery_neighborhood,
    order.delivery_city,
    order.delivery_complement
  ].filter(Boolean)
  return parts.join(', ') || 'Endereço não informado'
}

const formatDateTime = (value: string | undefined): string => {
  if (!value) return '-'
  const date = new Date(value)
  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const getStatusClass = (status: string): string => {
  const classes: Record<string, string> = {
    pending: 'bg-yellow-100 text-yellow-800',
    confirmed: 'bg-blue-100 text-blue-800',
    preparing: 'bg-purple-100 text-purple-800',
    ready: 'bg-indigo-100 text-indigo-800',
    out_for_delivery: 'bg-orange-100 text-orange-800',
    delivered: 'bg-green-100 text-green-800',
    cancelled: 'bg-red-100 text-red-800'
  }
  return classes[status] || 'bg-gray-100 text-gray-800'
}

const getStatusLabel = (status: string): string => {
  const labels: Record<string, string> = {
    pending: 'Pendente',
    confirmed: 'Confirmado',
    preparing: 'Em Preparo',
    ready: 'Pronto',
    out_for_delivery: 'Em Entrega',
    delivered: 'Entregue',
    cancelled: 'Cancelado'
  }
  return labels[status] || status
}

const logout = () => {
  localStorage.removeItem('driver_token')
  localStorage.removeItem('driver_name')
  localStorage.removeItem('driver_type')
  localStorage.removeItem('driver_index')
  router.push('/driver/login')
}

onMounted(() => {
  loadOrders()
})
</script>
